import { getActivePersona } from './personas'
import type { MockPersonaId, MockPlan } from './personas/types'

/**
 * Session-scoped so a Vite HMR or refresh does not drop a mock upgrade.
 * Keyed by persona so switching donor does not reuse another donor's plan.
 */
const STORAGE_KEY = 'swiftaid-mock-giving-plan'

type StoredPlans = Partial<Record<MockPersonaId, MockPlan>>

function readStoredPlans(): StoredPlans {
  if (typeof sessionStorage === 'undefined') return {}
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as StoredPlans
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writeStoredPlan(personaId: MockPersonaId, plan: MockPlan): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    const next = { ...readStoredPlans(), [personaId]: plan }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Private mode / quota: keep the in-memory value for this page only.
  }
}

function activePersonaId(): MockPersonaId {
  return getActivePersona().id as MockPersonaId
}

function initialPlan(): MockPlan {
  const persona = getActivePersona()
  const stored = readStoredPlans()[persona.id as MockPersonaId]
  return stored === 'paid' || stored === 'free' ? stored : persona.defaultPlan
}

let mockGivingPlan: MockPlan = initialPlan()

export function getMockGivingPlan(): MockPlan {
  return mockGivingPlan
}

export function setMockGivingPlan(plan: MockPlan): void {
  mockGivingPlan = plan
  writeStoredPlan(activePersonaId(), plan)
}

/** Reset paid/free state when switching mock donor persona. */
export function resetMockGivingPlan(plan?: MockPlan): void {
  mockGivingPlan = plan ?? getActivePersona().defaultPlan
  writeStoredPlan(activePersonaId(), mockGivingPlan)
}
